import { type ApolloError, useLazyQuery } from '@apollo/client';
import { audienceSegmentTagGroupOperator } from '@openx/components-deal-prompt/lib/constants.ts';
import type { UseDealPromptAudienceSearchTextFetch } from '@openx/components-deal-prompt/lib/utils/context/type.ts';
import { mapAudienceCriteriaToFiltersBody } from '@openx/components-deal-prompt/lib/utils/filters/filterMappers.ts';
import type { DealPromptFilters } from '@openx/components-deal-prompt/lib/utils/types.ts';
import type { AudienceSegmentCombinedResult, AudienceSegmentSearchInputDescriptor } from '@openx/types/gqlTypes.ts';
import type { AllTableCriteria } from '@openx/types/tableCriteria.ts';
import { useCallback } from 'react';
import { GetAudienceSegmentSearchTextDocument } from '../../../graphql/query/dealPrompt/GetAudienceSegmentSearchText.generated.tsx';
import { normalizeAudienceSegmentCombinedResult } from './normalizeAudienceSegmentSets.ts';

export const useDealPromptAudienceSearchTextFetch = (
  criteria: AllTableCriteria<DealPromptFilters>
): UseDealPromptAudienceSearchTextFetch => {
  const [fetchAudienceSegmentSearchText] = useLazyQuery(GetAudienceSegmentSearchTextDocument);

  const fetchDealPromptAudienceSearchText = useCallback(
    async (
      { query }: { query: string },
      abortSignal?: AbortSignal
    ): Promise<{
      generatedSearchInput: AudienceSegmentSearchInputDescriptor;
      combinedResult: AudienceSegmentCombinedResult;
      _error?: ApolloError;
    }> => {
      const result = await fetchAudienceSegmentSearchText({
        variables: {
          input: {
            inputText: query,
            filters: mapAudienceCriteriaToFiltersBody(criteria),
          },
        },
        context: {
          fetchOptions: {
            signal: abortSignal,
          },
        },
      });

      const generatedSearchInput = result?.data?.audienceSegmentSearchText?.generatedSearchInput ?? {
        tagGroups: { operator: audienceSegmentTagGroupOperator.AND, groups: [] },
        filters: {},
      };

      const combinedResult = result?.data?.audienceSegmentSearchText?.combinedResult ?? {
        segments: [],
        segmentsSets: [],
      };

      return {
        generatedSearchInput,
        combinedResult: normalizeAudienceSegmentCombinedResult(combinedResult),
        _error: result?.error,
      };
    },
    [fetchAudienceSegmentSearchText, criteria]
  );

  return {
    fetchDealPromptAudienceSearchText,
  };
};
